"use client";

import { type FC, type Ref, useMemo, useRef, useState } from "react";
import {
  Form,
  type FormFieldSchema,
  type FormOnSubmitFn,
  type UIAction,
  type UIAPIRequestAction,
  mergeRefs,
  useActions,
} from "@huub-web/ui";

import type { FieldSetTemplateProps } from "../FieldSetTemplate";
import type { TemplateCollection } from "../../types";
import { TemplatesParser } from "../../components";
import { useTemplates, useAgentFormAwareness } from "../../hooks";
import {
  InputVisibilityContext,
  type InputVisibilityState,
  type VisibilitySubscriber,
} from "../../contexts/InputVisibilityContext";
import { TemplateParentContext } from "../../contexts/TemplateParentContext";

export interface FormTemplateProps {
  /**
   * The unique identifier for the form.
   */
  id?: string;
  /**
   * The field sets to render.
   * @deprecated Use `templates` instead.
   */
  fieldSets?: FieldSetTemplateProps[];
  /**
   * The templates to render.
   */
  templates?: TemplateCollection;
  /**
   * Whether the form is disabled.
   */
  disabled?: boolean;
  /**
   * The action to execute when the form is submitted.
   */
  submitAction?: UIAction;
  /**
   * Callback fired when form is submitted
   */
  onSubmit?: (valid: boolean) => void;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLFormElement>;
}

export const FormTemplate: FC<FormTemplateProps> = ({
  id,
  templates,
  disabled,
  submitAction,
  onSubmit,
  ref,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  fieldSets: _fieldSets,
}) => {
  const { handleAction } = useActions();
  const { lockUI } = useTemplates();

  // Agent form awareness
  // ------------------------------

  const containerRef = useRef<HTMLFormElement>(null);
  const [currentFieldSchema, setCurrentFieldSchema] = useState<
    FormFieldSchema[]
  >([]);

  /**
   * When a FormFlowTemplate child is present it registers the same formId
   * with richer panel/wizard metadata. We skip registration entirely
   * to prevent the parent's deregisterUIContext from racing with
   * the child's registration during mount/unmount transitions.
   */
  const hasFormFlow = useMemo(
    () => templates?.some((t) => t.templateKey === "formFlow") ?? false,
    [templates],
  );

  useAgentFormAwareness({
    formId: id ?? "__new",
    formType: "form",
    fieldSchema: currentFieldSchema,
    skip: hasFormFlow,
  });

  // Input visibility tracking
  // ------------------------------

  const visibilityMapRef = useRef<Record<string, boolean>>({});
  const subscribersRef = useRef<Set<VisibilitySubscriber>>(new Set());

  const notifyVisibilitySubscribers = () => {
    subscribersRef.current.forEach((callback) => {
      callback(visibilityMapRef.current);
    });
  };

  const registerInputVisibility: InputVisibilityState["registerInputVisibility"] =
    (inputId, visible) => {
      if (visibilityMapRef.current[inputId] === visible) {
        return;
      }
      visibilityMapRef.current = {
        ...visibilityMapRef.current,
        [inputId]: visible,
      };
      notifyVisibilitySubscribers();
    };

  const unregisterInputVisibility: InputVisibilityState["unregisterInputVisibility"] =
    (inputId) => {
      if (!(inputId in visibilityMapRef.current)) {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [inputId]: _removed, ...rest } = visibilityMapRef.current;
      visibilityMapRef.current = rest;
      notifyVisibilitySubscribers();
    };

  const subscribeToVisibilityChanges: InputVisibilityState["subscribeToVisibilityChanges"] =
    (callback) => {
      subscribersRef.current.add(callback);
      return () => {
        subscribersRef.current.delete(callback);
      };
    };

  const getVisibilitySnapshot: InputVisibilityState["getVisibilitySnapshot"] =
    () => visibilityMapRef.current;

  // Context value
  const visibilityContextValue: InputVisibilityState = {
    registerInputVisibility,
    unregisterInputVisibility,
    subscribeToVisibilityChanges,
    getVisibilitySnapshot,
  };

  const parentContextValue = { id: id ?? "__new" };

  // Form submission
  // ------------------------------

  const handleSubmit: FormOnSubmitFn = (_e, valid, _errors, data) => {
    console.log("handleSubmit", valid, data);
    onSubmit?.(valid);
    if (submitAction) {
      if (submitAction.type === "apiRequest") {
        const apiAction = submitAction as UIAPIRequestAction;
        handleAction({
          ...apiAction,
          payload: {
            ...apiAction.payload,
            payload: { ...apiAction.payload.payload, ...data },
          },
        });
      } else {
        handleAction(submitAction);
      }
    }
  };

  return (
    <InputVisibilityContext.Provider value={visibilityContextValue}>
      <Form
        id={id}
        ref={mergeRefs(ref, containerRef)}
        gap="l"
        disabled={disabled || lockUI}
        trackDirty
        onSchemaChange={setCurrentFieldSchema}
        onSubmit={(e, valid, errors, data) => {
          if (lockUI) return;
          handleSubmit(e, valid, errors, data);
        }}
      >
        <TemplateParentContext.Provider value={parentContextValue}>
          {templates && templates.length > 0 && (
            <TemplatesParser templates={templates} level={1} />
          )}
        </TemplateParentContext.Provider>
      </Form>
    </InputVisibilityContext.Provider>
  );
};
